import { IS_SSR } from '../constants';
import type { OptimizelyActiveCampaign } from './types';

const FALLBACK_MS = 500;
const MAX_FALLBACK_ATTEMPTS = 120;

type RawCampaignState = {
    id: string;
    campaignName: string;
    variation?: { id: string; name: string };
};

type OptimizelyStateApi = {
    getCampaignStates?: (f: { isActive: boolean }) => Record<string, RawCampaignState>;
};

type OptimizelyPushEntry = {
    type: string;
    filter?: { type: string; name: string };
    handler?: () => void;
};

type OptimizelyClient =
    | (OptimizelyPushEntry[] & { get?: (key: string) => OptimizelyStateApi })
    | { get?: (key: string) => OptimizelyStateApi; push: (entry: OptimizelyPushEntry) => void };

type LifecycleListener = {
    filter: { type: 'lifecycle'; name: string };
    handler: () => void;
};

function getOptimizelyClient(): OptimizelyClient | undefined {
    if (IS_SSR) {
        return undefined;
    }

    return (globalThis as unknown as Window).optimizely as unknown as OptimizelyClient | undefined;
}

function getOptimizelyStateApi(): OptimizelyStateApi | undefined {
    return getOptimizelyClient()?.get?.('state');
}

export function isOptimizelyStateReady(): boolean {
    return typeof getOptimizelyStateApi()?.getCampaignStates === 'function';
}

/** Reads active Optimizely Web campaigns for the current session. Returns [] when unavailable. */
export function readActiveOptimizelyCampaigns(): OptimizelyActiveCampaign[] {
    const states = getOptimizelyStateApi()?.getCampaignStates?.({ isActive: true });

    if (!states) {
        return [];
    }

    return Object.values(states).flatMap(campaign => {
        if (!campaign.campaignName || !campaign.variation) {
            return [];
        }

        return [
            {
                campaignName: campaign.campaignName,
                campaignId: String(campaign.id),
                variationName: campaign.variation.name,
                variationId: String(campaign.variation.id),
            },
        ];
    });
}

function pushOptimizelyEntry(entry: OptimizelyPushEntry): void {
    const optimizely = getOptimizelyClient();

    if (!optimizely) {
        return;
    }

    if (Array.isArray(optimizely)) {
        optimizely.push(entry);
        return;
    }

    optimizely.push?.(entry);
}

function shouldStopFallback(campaigns: OptimizelyActiveCampaign[]): boolean {
    return campaigns.length > 0 || isOptimizelyStateReady();
}

/**
 * Invokes `onChange` when Optimizely becomes ready or campaign state may have changed.
 * Uses lifecycle listeners with a bounded fallback while the snippet is still loading.
 */
export function subscribeToOptimizelyCampaignChanges(onChange: () => void): () => void {
    const lifecycleListeners: LifecycleListener[] = [
        {
            filter: { type: 'lifecycle', name: 'initialized' },
            handler: onChange,
        },
        {
            filter: { type: 'lifecycle', name: 'activated' },
            handler: onChange,
        },
    ];

    let listenersRegistered = false;
    let fallbackIntervalId: ReturnType<typeof globalThis.setInterval> | undefined;
    let fallbackAttempts = 0;

    const registerLifecycleListeners = (): void => {
        if (listenersRegistered || !getOptimizelyClient()) {
            return;
        }

        for (const listener of lifecycleListeners) {
            pushOptimizelyEntry({
                type: 'addListener',
                filter: listener.filter,
                handler: listener.handler,
            });
        }

        listenersRegistered = true;
    };

    const removeLifecycleListeners = (): void => {
        if (!listenersRegistered) {
            return;
        }

        for (const listener of lifecycleListeners) {
            pushOptimizelyEntry({
                type: 'removeListener',
                filter: listener.filter,
                handler: listener.handler,
            });
        }

        listenersRegistered = false;
    };

    const stopFallback = (): void => {
        if (fallbackIntervalId !== undefined) {
            globalThis.clearInterval(fallbackIntervalId);
            fallbackIntervalId = undefined;
        }

        fallbackAttempts = 0;
    };

    const startFallback = (): void => {
        if (fallbackIntervalId !== undefined || shouldStopFallback(readActiveOptimizelyCampaigns())) {
            return;
        }

        fallbackAttempts = 0;
        fallbackIntervalId = globalThis.setInterval(() => {
            registerLifecycleListeners();
            onChange();

            if (shouldStopFallback(readActiveOptimizelyCampaigns())) {
                stopFallback();
                return;
            }

            if (++fallbackAttempts >= MAX_FALLBACK_ATTEMPTS) {
                stopFallback();
            }
        }, FALLBACK_MS);
    };

    registerLifecycleListeners();
    startFallback();

    return () => {
        removeLifecycleListeners();
        stopFallback();
    };
}
